import React, { useRef } from 'react';
import styled from 'styled-components';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { ConciergeRing, conciergePillCss, conciergeSurfaceCss } from 'src/concierge/concierge-card-chrome';
import { ConciergeVariables } from 'src/concierge/concierge-variables';
import { useConciergeThemeScope } from 'src/concierge/use-concierge-theme-scope';
import { useSeededConciergeNavigate } from 'src/dispensary/core-menu/components/header/concierge-nav-button/use-concierge-navigate';
import { useConciergeDisplayName } from 'src/concierge/use-concierge-display-name';

type ProductConciergePanelProps = {
  productId: string;
  productName: string;
};

export function ProductConciergePanel({ productId, productName }: ProductConciergePanelProps): JSX.Element {
  const displayName = useConciergeDisplayName();
  const openWithSeed = useSeededConciergeNavigate();
  const { overridesCss, scopeProps } = useConciergeThemeScope();
  const panelRef = useRef<HTMLElement>(null);
  const heading = displayName ? `Ask ${displayName}, your Virtual Budtender` : 'Ask the AI Concierge';

  return (
    <>
      <ConciergeVariables $tenantOverrides={overridesCss} />
      <Panel
        ref={panelRef}
        aria-labelledby='product-concierge-panel-heading'
        data-testid='product-concierge-panel'
        {...scopeProps}
      >
        <ConciergeRing targetRef={panelRef} testId='product-concierge-panel-ring' />
        <Heading id='product-concierge-panel-heading'>
          <Sparkle aria-hidden='true'>
            <BlackSparkleIcon width={24} height={24} fill='currentColor' />
          </Sparkle>
          {heading}
        </Heading>
        <Buttons>
          <Cta
            type='button'
            onClick={() =>
              openWithSeed(
                { kind: 'find_similar', message: `Find products similar to ${productName}`, context: { productId } },
                'pdp_find_similar'
              )
            }
          >
            Find Similar Products
          </Cta>
          <Cta
            type='button'
            onClick={() =>
              openWithSeed(
                { kind: 'teach_me', message: `Tell me about ${productName}`, context: { productId } },
                'pdp_teach_me'
              )
            }
          >
            Teach Me About This Product
          </Cta>
        </Buttons>
      </Panel>
    </>
  );
}

const Panel = styled.section`
  ${conciergeSurfaceCss}
  border-radius: var(--dcm-radius-cards, 8px);
  isolation: isolate;
  margin-bottom: 24px;
  padding: 18px;
  position: relative;
`;

const Heading = styled.h2`
  align-items: center;
  display: flex;
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--dcm-font-size-lg, 16px);
  font-weight: var(--dcm-font-weight-bold, 700);
  gap: 8px;
  margin: 0 0 12px;
  position: relative;
`;

const Sparkle = styled.span`
  color: var(--dcm-concierge-accent, #d4a843);
  display: flex;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const Cta = styled.button`
  ${conciergePillCss}
  margin-top: 0;
`;
